import React from 'react';
import { Form, Modal, notification } from 'antd';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from "../context/AuthContext";
import CheckCircleOutlined from '@ant-design/icons/lib/icons/CheckCircleOutlined';
import useSWR from 'swr';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);


const withAttendanceMechanicLogic = (Component: React.ComponentType<any>) => {
    return (props: any) => {
        const [form] = Form.useForm();
        const { userData } = useAuth();
        const [mechanicSearch, setMechanicSearch] = useState<string>("");
        const [mechanicList, setMechanicList] = useState<any[]>([]);
        const [rawMechanicList, setRawMechanicList] = useState<any[]>([]);
        const [warehouseCode, setWarehouseCode] = useState<string | null>(null);
        const [selectedDate, setSelectedDate] = useState<string | null>(null);
        const [isMechanicLocked, setIsMechanicLocked] = useState<boolean>(false);
        const fetcher = (url: string) => axios.get(url).then((res) => res.data);
        const { data: mechData, isLoading: isMechLoading } = useSWR(
            userData?.users ? `${process.env.REACT_APP_BACKEND_URL}/api/attendance-mechanic/user-mechanic-data?username=${userData.users}&search=${mechanicSearch}` : null,
            fetcher
        );

        const { data: attendedData } = useSWR(
            selectedDate && warehouseCode
                ? `${process.env.REACT_APP_BACKEND_URL}/api/attendance-mechanic/attended-list?date_trans=${selectedDate}&warehouse_code=${warehouseCode}`
                : null,
            fetcher,
            { refreshInterval: 1000 }
        );

        useEffect(() => {
            if (mechData && mechData.mechanic_list) {
                const options = mechData.mechanic_list.map((item: any) => ({
                    label: item.name,
                    value: item.code,
                    foreman: item.foreman_code
                }));

                const attendedCodes = attendedData?.attended_codes || [];
                const updatedList = options.map((mechanic: any) => {
                    const isAttended = attendedCodes.includes(mechanic.value);
                    return {
                        ...mechanic,
                        disabled: isAttended,
                        label: isAttended ? (
                            <span style={{ color: 'green', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {mechanic.label} <CheckCircleOutlined />
                            </span>
                        ) : (
                            mechanic.label
                        )
                    };
                });
                setMechanicList(updatedList);

                if (mechData.warehouse_name) form.setFieldsValue({ warehouse: mechData.warehouse_name });
                if (mechData.warehouse_code) setWarehouseCode(mechData.warehouse_code);
                if (mechData.server_date && !selectedDate) {
                    form.setFieldsValue({ date_trans: dayjs(mechData.server_date, 'DD/MM/YYYY') });
                    setSelectedDate(mechData.server_date);
                }

                const hasAuthority03 = mechData.otoritas?.split(';').includes("03");
                setIsMechanicLocked(!hasAuthority03);

                if (!hasAuthority03 && options.length > 0 && !form.getFieldValue('name_mechanic')) {
                    const target = options[0];
                    form.setFieldsValue({ name_mechanic: target.value, code_mechanic: target.value, foreman: target.foreman });
                }
            }
        }, [mechData, attendedData]);

        const handleSearchMechanic = (value: string) => {
            setMechanicSearch(value);
        };

        useEffect(() => {
            const fetchMechanicName = async () => {
                if (userData?.users) {
                    try {
                        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/attendance-mechanic/user-mechanic-data`, {
                            params: { username: userData.users }
                        });

                        if (response.data && response.data.server_date) {
                            const serverDateStr = response.data.server_date;
                            const serverDateObj = dayjs(serverDateStr, 'DD/MM/YYYY');

                            form.setFieldsValue({
                                date_trans: serverDateObj
                            });

                            setSelectedDate(serverDateStr);
                        }

                        if (response.data && response.data.warehouse_code) {
                            setWarehouseCode(response.data.warehouse_code);
                        }

                        if (response.data && response.data.mechanic_list && response.data.mechanic_list.length > 0) {
                            const options = response.data.mechanic_list.map((item: any) => ({
                                label: item.name,
                                value: item.code,
                                foreman: item.foreman_code
                            }));
                            const userOtoritas = response.data.otoritas;
                            const userKodeGrup1 = response.data.kode_grup1;
                            const hasAuthority03 = userOtoritas && userOtoritas.split(';').includes("03");

                            if (!hasAuthority03) {
                                const filteredOptions = options.filter((opt: any) => opt.value === userKodeGrup1);
                                setRawMechanicList(filteredOptions);
                                setMechanicList(filteredOptions);
                                setIsMechanicLocked(true);

                                if (filteredOptions.length > 0) {
                                    const targetMechanic = filteredOptions[0];
                                    form.setFieldsValue({
                                        name_mechanic: targetMechanic.value,
                                        code_mechanic: targetMechanic.value,
                                        foreman: targetMechanic.foreman,
                                        warehouse: response.data.warehouse_name
                                    });
                                } else {

                                    form.setFieldsValue({
                                        warehouse: response.data.warehouse_name
                                    });
                                }
                            } else {
                                setRawMechanicList(options);
                                setMechanicList(options);
                                setIsMechanicLocked(false);
                                form.setFieldsValue({
                                    warehouse: response.data.warehouse_name
                                });
                            }
                        } else {
                            setMechanicList([]);
                            setRawMechanicList([]);
                            if (response.data && response.data.warehouse_name) {
                                form.setFieldsValue({
                                    warehouse: response.data.warehouse_name
                                });
                            }
                            notification.info({
                                message: 'Info',
                                description: 'Tidak ada data mekanik untuk warehouse anda.',
                                duration: 3,
                            });
                        }

                    } catch (error) {
                        console.error("Error fetching mechanic data:", error);
                    }
                }
            };

            fetchMechanicName();
        }, [userData, form]);

        const handleDateChange = async (dateString: string) => {
            if (!dateString || !warehouseCode) return;
            setSelectedDate(dateString);
            form.setFieldsValue({ name_mechanic: undefined, code_mechanic: undefined, foreman: undefined });
        };

        const handleSubmit = async () => {
            try {
                const values = await form.validateFields();
                const formattedDate = values.date_trans ? values.date_trans.format('DD/MM/YYYY') : null;
                const today = new Date();
                const dd = String(today.getDate()).padStart(2, '0');
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const yyyy = today.getFullYear();
                const currentDateLoop = `${dd}/${mm}/${yyyy}`;

                if (formattedDate !== currentDateLoop) {
                    notification.error({
                        message: 'Peringatan',
                        description: 'Anda tidak bisa melakukan absen di esok hari',
                    });
                    return;
                }

                Modal.confirm({
                    title: 'Konfirmasi',
                    content: 'Apakah sudah sesuai?',
                    okText: 'Ya',
                    cancelText: 'Tidak',
                    onOk: async () => {
                        try {
                            const payload = {
                                date_trans: formattedDate,
                                name_mechanic: values.name_mechanic,
                                code_mechanic: values.code_mechanic,
                                foreman: values.foreman,
                                warehouse: warehouseCode,
                            };

                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/attendance-mechanic/save`, payload);
                            notification.success({
                                message: 'Success',
                                description: 'Data attendance mechanic berhasil disimpan.',
                            });

                            form.resetFields(['name_mechanic', 'code_mechanic', 'foreman']);

                            if (isMechanicLocked && mechanicList.length > 0) {
                                const targetMechanic = mechanicList[0];
                                form.setFieldsValue({
                                    name_mechanic: targetMechanic.value,
                                    code_mechanic: targetMechanic.value,
                                    foreman: targetMechanic.foreman
                                });
                            }
                        } catch (error: any) {
                            if (error.response && error.response.status === 409) {
                                notification.error({
                                    message: 'Peringatan',
                                    description: 'Mekanik sudah melakukan absen, harap pilih mekanik lain',
                                });
                            } else {
                                console.error("Error saving data:", error);
                                notification.error({
                                    message: 'Error',
                                    description: 'Gagal menyimpan data.',
                                });
                            }
                        }
                    }
                });

            } catch (error: any) {
                if (error.errorFields) {
                    console.log('Validation Failed:', error);
                }
            }
        };

        return (
            <Component
                {...props}
                form={form}
                mechanicList={mechanicList}
                handleSubmit={handleSubmit}
                handleDateChange={handleDateChange}
                isMechanicLocked={isMechanicLocked}
                handleSearchMechanic={handleSearchMechanic}
                isMechLoading={isMechLoading}
            />
        );
    };
};

export default withAttendanceMechanicLogic;